// src/config/access.js
//
// Everything about who gets past the gate lives here, behind one async
// function. Nothing else in the app knows how a code is checked — the gate
// component just awaits `verifyAccessCode()` and reacts to the result.
//
// That indirection is the point. Swapping the shared code for a real
// backend later means editing this file only: change MODE to "remote",
// point REMOTE_ENDPOINT at your worker, and every gate on the site starts
// checking server-side without a single component changing.
//
// -----------------------------------------------------------------------
// WHAT THE CURRENT MODE IS, HONESTLY
//
// MODE "shared-code" compares against a string that ships inside the JS
// bundle. Anyone who opens devtools can read it. It stops a stranger who
// stumbles on a link; it does not stop anyone actually trying.
//
// That's an acceptable trade for what it currently guards — an on-device
// estimate that costs nothing to run. It would not be acceptable for paid
// content. If this ever gates something you sell, switch MODE to "remote"
// BEFORE launch, not after.
// -----------------------------------------------------------------------

/** "shared-code" | "remote" */
export const MODE = "shared-code";

/**
 * Used when MODE === "shared-code".
 *
 * Change this one string to rotate the code. Anyone holding the old one
 * loses access on the next deploy; unlock state is per-session, so nobody
 * stays in on a stale value.
 */
export const ACCESS_CODE = "admin";

/**
 * Used when MODE === "remote". Should accept POST { code } and return
 * { ok: true } or a 401/403.
 *
 * A Cloudflare Pages Function at functions/api/unlock.js is the natural
 * home — same deployment, no separate service, and the real code lives in
 * an environment variable instead of the bundle.
 */
export const REMOTE_ENDPOINT = "/api/unlock";

/**
 * How long an unlock survives.
 *
 * "session" clears when the tab closes, which suits a QR code scanned once
 * on set. "persistent" survives restarts, which suits a paid unlock.
 */
export const PERSISTENCE = "session";

function store() {
  try {
    return PERSISTENCE === "persistent" ? window.localStorage : window.sessionStorage;
  } catch (e) {
    // Private browsing, or storage disabled entirely.
    return null;
  }
}

export function isUnlocked(storageKey) {
  const s = store();
  if (!s) return false;
  try {
    return s.getItem(storageKey) === "true";
  } catch (e) {
    return false;
  }
}

export function rememberUnlock(storageKey) {
  const s = store();
  if (!s) return;
  try {
    s.setItem(storageKey, "true");
  } catch (e) {
    // Unlock still holds for this render; it just won't survive a reload.
  }
}

/**
 * The single entry point every gate uses.
 *
 * @param {string} code
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export async function verifyAccessCode(code) {
  const trimmed = String(code || "").trim();
  if (!trimmed) return { ok: false, message: "Enter the code to continue." };

  if (MODE === "remote") {
    try {
      const res = await fetch(REMOTE_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ code: trimmed }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        return data && data.ok === false
          ? { ok: false, message: data.message || "That code isn't right." }
          : { ok: true };
      }
      if (res.status === 401 || res.status === 403) {
        return { ok: false, message: "That code isn't right — try again." };
      }
      return { ok: false, message: "Couldn't check that code just now. Try again in a moment." };
    } catch (e) {
      // Offline is a real scenario here — people use this on set, sometimes
      // with no signal. Say so plainly rather than implying a wrong code.
      return { ok: false, message: "No connection, so the code can't be checked right now." };
    }
  }

  return trimmed.toLowerCase() === ACCESS_CODE.toLowerCase()
    ? { ok: true }
    : { ok: false, message: "That code isn't right — try again." };
}
